import React, { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { DashboardHeader } from '../components/dashboard/DashboardHeader';
import { DashboardSidebar } from '../components/dashboard/DashboardSidebar';
import { BookmarkIcon, BellIcon, DownloadIcon, ShareIcon, ChevronLeftIcon, FileTextIcon, MapPinIcon, BuildingIcon, CalendarIcon, ClockIcon, CheckCircleIcon, AlertCircleIcon, InfoIcon, DollarSignIcon, UsersIcon, PhoneIcon, MailIcon, GlobeIcon, TagIcon, FileIcon, PaperclipIcon, StarIcon, BarChartIcon, EyeIcon, MessageSquareIcon, HelpCircleIcon, PlusIcon, ArrowRightIcon, ChevronDownIcon, ChevronRightIcon, CheckIcon, XIcon, PrinterIcon, ExternalLinkIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
// Types
interface TenderDocument {
  id: string;
  name: string;
  type: string;
  size: string;
  uploadDate: Date;
  isPremium: boolean;
}
interface TenderContact {
  name: string;
  role: string;
  email: string;
  phone?: string;
}
interface TenderRequirement {
  id: string;
  category: string;
  description: string;
  isRequired: boolean;
}
interface SimilarTender {
  id: string;
  title: string;
  organization: string;
  country: string;
  expiryDate: Date;
  matchScore?: number;
}
interface TenderComment {
  id: string;
  user: {
    name: string;
    avatar: string;
    role: string;
  };
  date: Date;
  text: string;
  isPrivate: boolean;
}
interface TenderLot {
  id: string;
  title: string;
  description: string;
  estimatedValue?: string;
  requirementSummary?: string;
}
interface TenderTimeline {
  date: Date;
  title: string;
  description: string;
  isCompleted: boolean;
  isPending?: boolean;
}
// Mock data for the tender details
const tenderDetails = {
  id: '07182025',
  title: "Travaux de génie civile pour l'Hôpital Régional de Menzel Bourguiba",
  reference: '98/2025/TUNEPS/S2025100798',
  description: "Travaux de génie civile pour la Direction Régionale de Nabeul Bouargoub comprenant la construction d'un bâtiment administratif de deux étages et l'aménagement des espaces extérieurs selon les normes d'accessibilité et environnementales en vigueur.",
  type: 'Avis de consultation',
  sector: 'BTP & Construction',
  subSector: 'Génie civil',
  activity: 'Bâtiment - Entreprise générale',
  location: 'Menzel Bourguiba',
  country: 'Tunisie',
  organization: 'Hôpital Régional de Menzel Bourguiba',
  organizationLogo: 'https://via.placeholder.com/150',
  source: 'TUNEPS',
  sourceUrl: 'https://www.tuneps.tn',
  publishDate: new Date('2025-10-04T17:04:00'),
  expiryDate: new Date('2025-10-07T09:00:00'),
  openingDate: new Date('2025-10-07T10:00:00'),
  budget: '450 000 TND',
  caution: '--',
  status: 'open' as 'open' | 'closing_soon' | 'closed',
  premium: true,
  matchScore: 92,
  viewCount: 47,
  applicationCount: 5,
  documentCount: 3,
  isSaved: false,
  hasAlert: false,
  isPublic: true,
  nature: 'Public',
  lots: [{
    id: '1',
    title: 'Travaux génie civile',
    description: 'Exécution des travaux de fondation, gros œuvre, et structure en béton armé selon les plans et cahier des charges.',
    estimatedValue: '300 000 TND',
    requirementSummary: 'Entreprise qualifiée B3 Catégorie 4 ou plus'
  }],
  documents: [{
    id: 'doc1',
    name: 'Cahier des charges techniques',
    type: 'PDF',
    size: '2.4 MB',
    uploadDate: new Date('2025-10-04'),
    isPremium: false
  }, {
    id: 'doc2',
    name: 'Plans architecturaux',
    type: 'ZIP',
    size: '15.7 MB',
    uploadDate: new Date('2025-10-04'),
    isPremium: true
  }, {
    id: 'doc3',
    name: 'Bordereau des prix',
    type: 'XLSX',
    size: '1.2 MB',
    uploadDate: new Date('2025-10-04'),
    isPremium: true
  }],
  contacts: [{
    name: 'Ahmed Ben Salah',
    role: 'Responsable des marchés publics',
    email: 'a.bensalah@hopital-mb.tn',
    phone: '+216 72 519 800'
  }],
  requirements: [{
    id: 'req1',
    category: 'Qualifications',
    description: 'Agrément B3 Catégorie 4 ou plus',
    isRequired: true
  }, {
    id: 'req2',
    category: 'Expérience',
    description: 'Minimum de 5 projets similaires réalisés dans les 5 dernières années',
    isRequired: true
  }, {
    id: 'req3',
    category: "Chiffre d'affaires",
    description: 'Minimum de 900 000 TND en moyenne sur les 3 derniers exercices',
    isRequired: true
  }, {
    id: 'req4',
    category: 'Personnel',
    description: "Un ingénieur en génie civil avec 10 ans d'expérience minimum",
    isRequired: true
  }, {
    id: 'req5',
    category: 'Certification',
    description: 'ISO 9001',
    isRequired: false
  }],
  timeline: [{
    date: new Date('2025-10-04T17:04:00'),
    title: 'Publication',
    description: "Publication de l'avis de consultation",
    isCompleted: true
  }, {
    date: new Date('2025-10-07T09:00:00'),
    title: 'Date limite',
    description: 'Date limite de remise des offres',
    isCompleted: false,
    isPending: true
  }, {
    date: new Date('2025-10-07T10:00:00'),
    title: 'Ouverture des plis',
    description: "Séance publique d'ouverture des offres",
    isCompleted: false
  }, {
    date: new Date('2025-10-20'),
    title: 'Évaluation',
    description: 'Évaluation technique et financière des offres',
    isCompleted: false
  }, {
    date: new Date('2025-11-05'),
    title: 'Attribution',
    description: 'Attribution provisoire du marché',
    isCompleted: false
  }],
  similarTenders: [{
    id: '08152025',
    title: "Construction d'un centre de santé de base à Bizerte",
    organization: 'Direction Régionale de la Santé de Bizerte',
    country: 'Tunisie',
    expiryDate: new Date('2025-10-15'),
    matchScore: 87
  }, {
    id: '09102025',
    title: "Réhabilitation de l'hôpital local de Menzel Temime",
    organization: 'Direction Régionale de la Santé de Nabeul',
    country: 'Tunisie',
    expiryDate: new Date('2025-10-20'),
    matchScore: 85
  }, {
    id: '07232025',
    title: "Travaux de génie civil pour l'extension du service d'urgence",
    organization: 'Hôpital Universitaire Habib Bourguiba Sfax',
    country: 'Tunisie',
    expiryDate: new Date('2025-10-12'),
    matchScore: 82
  }],
  comments: [{
    id: 'comment1',
    user: {
      name: 'Mehdi Fathallah',
      avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
      role: 'Directeur Commercial'
    },
    date: new Date('2025-10-05T09:23:00'),
    text: 'Projet intéressant qui correspond à notre stratégie de développement dans le secteur hospitalier. Préparons une offre compétitive.',
    isPrivate: true
  }, {
    id: 'comment2',
    user: {
      name: 'Sarra Mansour',
      avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
      role: 'Responsable Technique'
    },
    date: new Date('2025-10-05T11:45:00'),
    text: "J'ai analysé le cahier des charges, nous avons toutes les qualifications requises. Je vais préparer la partie technique de l'offre.",
    isPrivate: true
  }]
};
// Helper functions
const formatDate = (date: Date): string => {
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};
const formatDateTime = (date: Date): string => {
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};
const calculateDaysRemaining = (date: Date): number => {
  const today = new Date();
  const diffTime = date.getTime() - today.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};
const isClosingSoon = (date: Date): boolean => {
  return calculateDaysRemaining(date) <= 3 && calculateDaysRemaining(date) > 0;
};
// Main component
export function TenderDetailsPage() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'documents' | 'requirements' | 'contacts' | 'comments'>('overview');
  const [showAllRequirements, setShowAllRequirements] = useState(false);
  const [newComment, setNewComment] = useState('');
  const navigate = useNavigate();
  // Mock user data
  const user = {
    name: 'FATHALLAH Mehdi',
    company: 'Tunipages',
    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
    email: 'mehdi.fathallah@tunipages.com',
    phone: '+33 7 53 29 34 19',
    plan: 'Premium Afrique'
  };
  // Toggle save/bookmark
  const toggleSave = () => {
    // In a real application, this would call an API to save the tender
    console.log(`Toggle save for tender ${tenderDetails.id}`);
  };
  // Toggle alert
  const toggleAlert = () => {
    // In a real application, this would call an API to set an alert
    console.log(`Toggle alert for tender ${tenderDetails.id}`);
  };
  // Share tender
  const shareTender = () => {
    // In a real application, this would open a share dialog
    console.log(`Share tender ${tenderDetails.id}`);
  };
  // Download tender
  const downloadTender = () => {
    // In a real application, this would download the tender details
    console.log(`Download tender ${tenderDetails.id}`);
  };
  // Print tender
  const printTender = () => {
    window.print();
  };
  // Add comment
  const addComment = () => {
    if (newComment.trim()) {
      // In a real application, this would call an API to add a comment
      console.log(`Add comment to tender ${tenderDetails.id}: ${newComment}`);
      setNewComment('');
    }
  };
  const daysRemaining = calculateDaysRemaining(tenderDetails.expiryDate);
  const closingSoon = isClosingSoon(tenderDetails.expiryDate);
  const isExpired = daysRemaining <= 0;
  return <div className="min-h-screen bg-gray-50 print:bg-white" data-id="element-4008">
      {/* Mobile sidebar backdrop */}
      {sidebarOpen && <div className="fixed inset-0 bg-gray-800 bg-opacity-50 z-20 md:hidden" onClick={() => setSidebarOpen(false)} data-id="element-4009" />}
      {/* Sidebar */}
      <DashboardSidebar open={sidebarOpen} setOpen={setSidebarOpen} data-id="element-4010" />
      {/* Main content */}
      <div className="md:pl-64" data-id="element-4011">
        {/* Header */}
        <DashboardHeader user={user} onMenuClick={() => setSidebarOpen(true)} data-id="element-4012" />
        {/* Main content */}
        <main className="py-6 px-4 sm:px-6 lg:px-8 print:p-0" data-id="element-4013">
          {/* Back button */}
          <div className="mb-6 print:hidden" data-id="element-4014">
            <button onClick={() => navigate(-1)} className="flex items-center text-gray-600 hover:text-blue-600" data-id="element-4015">
              <ChevronLeftIcon size={20} className="mr-1" data-id="element-4016" />
              <span data-id="element-4017">Retour aux résultats</span>
            </button>
          </div>
          {/* Header section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6 overflow-hidden" data-id="element-4018">
            <div className="p-6 border-b border-gray-100" data-id="element-4019">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4" data-id="element-4020">
                <div className="flex-grow" data-id="element-4021">
                  {/* Status badges */}
                  <div className="flex flex-wrap gap-2 mb-3" data-id="element-4022">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700" data-id="element-4023">
                      {tenderDetails.type}
                    </span>
                    {closingSoon && !isExpired && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-orange-50 text-orange-700 flex items-center" data-id="element-4024">
                        <ClockIcon size={12} className="mr-1" data-id="element-4025" />
                        Clôture proche
                      </span>}
                    {isExpired && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-red-50 text-red-700 flex items-center" data-id="element-4026">
                        <AlertCircleIcon size={12} className="mr-1" data-id="element-4027" />
                        Expiré
                      </span>}
                    {tenderDetails.premium && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 flex items-center" data-id="element-4028">
                        <StarIcon size={12} className="mr-1" data-id="element-4029" />
                        Premium
                      </span>}
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 flex items-center" data-id="element-4030">
                      <GlobeIcon size={12} className="mr-1" data-id="element-4031" />
                      {tenderDetails.nature}
                    </span>
                  </div>
                  {/* Title */}
                  <h1 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-4032">
                    {tenderDetails.title}
                  </h1>
                  {/* Reference */}
                  <div className="flex items-center text-sm text-gray-500 mb-3" data-id="element-4033">
                    <span className="font-medium text-gray-700 mr-2" data-id="element-4034">
                      Référence:
                    </span>
                    {tenderDetails.reference}
                  </div>
                  {/* Organization and location */}
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 text-sm mb-4" data-id="element-4035">
                    <div className="flex items-center text-gray-700" data-id="element-4036">
                      <BuildingIcon size={16} className="mr-1.5 text-gray-500" data-id="element-4037" />
                      {tenderDetails.organization}
                    </div>
                    <div className="flex items-center text-gray-700" data-id="element-4038">
                      <MapPinIcon size={16} className="mr-1.5 text-gray-500" data-id="element-4039" />
                      {tenderDetails.location}, {tenderDetails.country}
                    </div>
                  </div>
                  {/* Key dates */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-4040">
                    <div className="bg-gray-50 p-3 rounded-lg" data-id="element-4041">
                      <div className="text-xs text-gray-500 mb-1 flex items-center" data-id="element-4042">
                        <CalendarIcon size={12} className="mr-1" data-id="element-4043" />
                        Date de publication
                      </div>
                      <div className="text-sm font-medium text-gray-800" data-id="element-4044">
                        {formatDateTime(tenderDetails.publishDate)}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg" data-id="element-4045">
                      <div className="text-xs text-gray-500 mb-1 flex items-center" data-id="element-4046">
                        <ClockIcon size={12} className="mr-1" data-id="element-4047" />
                        Date limite
                      </div>
                      <div className={`text-sm font-medium ${isExpired ? 'text-red-600' : closingSoon ? 'text-orange-600' : 'text-gray-800'}`} data-id="element-4048">
                        {formatDateTime(tenderDetails.expiryDate)}
                        {!isExpired && <span className="ml-1 text-xs" data-id="element-4049">
                            ({daysRemaining} jour
                            {daysRemaining !== 1 ? 's' : ''})
                          </span>}
                      </div>
                    </div>
                    <div className="bg-gray-50 p-3 rounded-lg" data-id="element-4050">
                      <div className="text-xs text-gray-500 mb-1 flex items-center" data-id="element-4051">
                        <InfoIcon size={12} className="mr-1" data-id="element-4052" />
                        Ouverture des plis
                      </div>
                      <div className="text-sm font-medium text-gray-800" data-id="element-4053">
                        {formatDateTime(tenderDetails.openingDate)}
                      </div>
                    </div>
                  </div>
                </div>
                {/* Right side - Score and actions */}
                <div className="flex flex-col items-center space-y-3" data-id="element-4054">
                  {/* Match score for premium users */}
                  {tenderDetails.premium && tenderDetails.matchScore && <div className="flex flex-col items-center" data-id="element-4055">
                      <div className={`w-20 h-20 rounded-full flex items-center justify-center text-white font-bold text-xl ${tenderDetails.matchScore >= 80 ? 'bg-green-500' : tenderDetails.matchScore >= 60 ? 'bg-yellow-500' : 'bg-orange-500'}`} data-id="element-4056">
                        {tenderDetails.matchScore}%
                      </div>
                      <div className="text-sm text-gray-600 mt-1" data-id="element-4057">
                        Correspondance
                      </div>
                    </div>}
                  {/* Action buttons */}
                  <div className="flex flex-wrap justify-center gap-2 print:hidden" data-id="element-4058">
                    <button className={`p-2 rounded-full ${tenderDetails.isSaved ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`} onClick={toggleSave} title={tenderDetails.isSaved ? 'Retirer des favoris' : 'Ajouter aux favoris'} data-id="element-4059">
                      <BookmarkIcon size={20} data-id="element-4060" />
                    </button>
                    <button className={`p-2 rounded-full ${tenderDetails.hasAlert ? 'bg-blue-50 text-blue-600' : 'bg-gray-50 text-gray-500 hover:bg-gray-100'}`} onClick={toggleAlert} title={tenderDetails.hasAlert ? "Supprimer l'alerte" : 'Créer une alerte'} data-id="element-4061">
                      <BellIcon size={20} data-id="element-4062" />
                    </button>
                    <button className="p-2 rounded-full bg-gray-50 text-gray-500 hover:bg-gray-100" onClick={shareTender} title="Partager" data-id="element-4063">
                      <ShareIcon size={20} data-id="element-4064" />
                    </button>
                    <button className="p-2 rounded-full bg-gray-50 text-gray-500 hover:bg-gray-100" onClick={downloadTender} title="Télécharger" data-id="element-4065">
                      <DownloadIcon size={20} data-id="element-4066" />
                    </button>
                    <button className="p-2 rounded-full bg-gray-50 text-gray-500 hover:bg-gray-100" onClick={printTender} title="Imprimer" data-id="element-4067">
                      <PrinterIcon size={20} data-id="element-4068" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {/* Tabs navigation */}
            <div className="border-b border-gray-200 print:hidden" data-id="element-4069">
              <nav className="flex -mb-px overflow-x-auto" data-id="element-4070">
                <button onClick={() => setActiveTab('overview')} className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'overview' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4071">
                  Aperçu
                </button>
                <button onClick={() => setActiveTab('documents')} className={`py-4 px-6 text-sm font-medium border-b-2 flex items-center ${activeTab === 'documents' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4072">
                  Documents
                  <span className="ml-2 bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs" data-id="element-4073">
                    {tenderDetails.documents.length}
                  </span>
                </button>
                <button onClick={() => setActiveTab('requirements')} className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'requirements' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4074">
                  Exigences
                </button>
                <button onClick={() => setActiveTab('contacts')} className={`py-4 px-6 text-sm font-medium border-b-2 ${activeTab === 'contacts' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4075">
                  Contacts
                </button>
                <button onClick={() => setActiveTab('comments')} className={`py-4 px-6 text-sm font-medium border-b-2 flex items-center ${activeTab === 'comments' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`} data-id="element-4076">
                  Commentaires
                  <span className="ml-2 bg-gray-100 text-gray-700 rounded-full px-2 py-0.5 text-xs" data-id="element-4077">
                    {tenderDetails.comments.length}
                  </span>
                </button>
              </nav>
            </div>
            {/* Tab content */}
            <div className="p-6" data-id="element-4078">
              {/* Overview tab */}
              {(activeTab === 'overview' || !activeTab) && <div className="space-y-8" data-id="element-4079">
                  {/* Description section */}
                  <section data-id="element-4080">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4081">
                      Description
                    </h2>
                    <div className="bg-gray-50 p-4 rounded-lg text-gray-700" data-id="element-4082">
                      <p data-id="element-4083">{tenderDetails.description}</p>
                    </div>
                  </section>
                  {/* Timeline section */}
                  <section data-id="element-4084">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4085">
                      Calendrier du marché
                    </h2>
                    <div className="relative" data-id="element-4086">
                      <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200" data-id="element-4087"></div>
                      <div className="space-y-4" data-id="element-4088">
                        {tenderDetails.timeline.map((event, index) => <div key={index} className="flex" data-id="element-4089">
                            <div className="relative flex items-center justify-center" data-id="element-4090">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center z-10 ${event.isCompleted ? 'bg-green-100 text-green-600' : event.isPending ? 'bg-yellow-100 text-yellow-600' : 'bg-gray-100 text-gray-400'}`} data-id="element-4091">
                                {event.isCompleted ? <CheckIcon size={16} data-id="element-4092" /> : <span className="text-xs font-semibold" data-id="element-4093">
                                    {index + 1}
                                  </span>}
                              </div>
                            </div>
                            <div className="ml-4 bg-white p-3 rounded-lg border border-gray-100 shadow-sm flex-grow" data-id="element-4094">
                              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-1" data-id="element-4095">
                                <span className="font-medium text-gray-900" data-id="element-4096">
                                  {event.title}
                                </span>
                                <span className={`text-sm ${event.isPending ? 'text-yellow-600 font-medium' : 'text-gray-500'}`} data-id="element-4097">
                                  {formatDate(event.date)}
                                </span>
                              </div>
                              <p className="text-sm text-gray-600" data-id="element-4098">
                                {event.description}
                              </p>
                            </div>
                          </div>)}
                      </div>
                    </div>
                  </section>
                  {/* Lots section */}
                  <section data-id="element-4099">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4100">
                      Lots ({tenderDetails.lots.length})
                    </h2>
                    <div className="space-y-4" data-id="element-4101">
                      {tenderDetails.lots.map(lot => <div key={lot.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden" data-id="element-4102">
                          <div className="p-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center" data-id="element-4103">
                            <div className="font-medium text-gray-800" data-id="element-4104">
                              Lot {lot.id}: {lot.title}
                            </div>
                            {lot.estimatedValue && <div className="text-sm bg-blue-50 text-blue-700 px-2 py-1 rounded-md flex items-center" data-id="element-4105">
                                <DollarSignIcon size={14} className="mr-1" data-id="element-4106" />
                                {lot.estimatedValue}
                              </div>}
                          </div>
                          <div className="p-4" data-id="element-4107">
                            <p className="text-sm text-gray-700 mb-3" data-id="element-4108">
                              {lot.description}
                            </p>
                            {lot.requirementSummary && <div className="text-sm text-gray-600 flex items-start" data-id="element-4109">
                                <InfoIcon size={16} className="mr-2 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-4110" />
                                <span data-id="element-4111">
                                  Exigence principale: {lot.requirementSummary}
                                </span>
                              </div>}
                          </div>
                        </div>)}
                    </div>
                  </section>
                  {/* Additional details section */}
                  <section data-id="element-4112">
                    <h2 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-4113">
                      Informations complémentaires
                    </h2>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4114">
                      <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4115">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-4116">
                          Secteur d'activité
                        </div>
                        <div className="text-gray-800 flex items-center" data-id="element-4117">
                          <TagIcon size={16} className="mr-2 text-blue-500" data-id="element-4118" />
                          {tenderDetails.sector}
                          {tenderDetails.subSector && <span className="text-gray-600" data-id="element-4119">
                              {' > '}
                              {tenderDetails.subSector}
                            </span>}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4120">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-4121">
                          Activité principale
                        </div>
                        <div className="text-gray-800 flex items-center" data-id="element-4122">
                          <TagIcon size={16} className="mr-2 text-blue-500" data-id="element-4123" />
                          {tenderDetails.activity}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4124">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-4125">
                          Budget estimé
                        </div>
                        <div className="text-gray-800 flex items-center" data-id="element-4126">
                          <DollarSignIcon size={16} className="mr-2 text-green-500" data-id="element-4127" />
                          {tenderDetails.budget}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4128">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-4129">
                          Caution
                        </div>
                        <div className="text-gray-800 flex items-center" data-id="element-4130">
                          <DollarSignIcon size={16} className="mr-2 text-green-500" data-id="element-4131" />
                          {tenderDetails.caution}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4132">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-4133">Source</div>
                        <div className="text-gray-800 flex items-center" data-id="element-4134">
                          <GlobeIcon size={16} className="mr-2 text-blue-500" data-id="element-4135" />
                          {tenderDetails.source}
                          {tenderDetails.sourceUrl && <a href={tenderDetails.sourceUrl} target="_blank" rel="noopener noreferrer" className="ml-2 text-blue-600 hover:text-blue-800 text-sm flex items-center" data-id="element-4136">
                              Voir sur le site{' '}
                              <ExternalLinkIcon size={12} className="ml-1" data-id="element-4137" />
                            </a>}
                        </div>
                      </div>
                      <div className="bg-gray-50 p-4 rounded-lg" data-id="element-4138">
                        <div className="text-sm text-gray-500 mb-1" data-id="element-4139">
                          Statistiques
                        </div>
                        <div className="text-gray-800" data-id="element-4140">
                          <div className="flex items-center mb-1" data-id="element-4141">
                            <EyeIcon size={14} className="mr-2 text-gray-400" data-id="element-4142" />
                            <span data-id="element-4143">{tenderDetails.viewCount} vues</span>
                          </div>
                          <div className="flex items-center" data-id="element-4144">
                            <UsersIcon size={14} className="mr-2 text-gray-400" data-id="element-4145" />
                            <span data-id="element-4146">
                              {tenderDetails.applicationCount} soumissionnaires
                              potentiels
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </section>
                </div>}
              {/* Documents tab */}
              {activeTab === 'documents' && <div data-id="element-4147">
                  <div className="flex justify-between items-center mb-4" data-id="element-4148">
                    <h2 className="text-lg font-semibold text-gray-900" data-id="element-4149">
                      Documents ({tenderDetails.documents.length})
                    </h2>
                    <Button variant="outline" size="sm" className="flex items-center" data-id="element-4150">
                      <DownloadIcon size={16} className="mr-2" data-id="element-4151" />
                      Télécharger tous
                    </Button>
                  </div>
                  <div className="bg-gray-50 p-4 rounded-lg mb-6 flex items-center" data-id="element-4152">
                    <InfoIcon size={18} className="text-blue-500 mr-3 flex-shrink-0" data-id="element-4153" />
                    <p className="text-sm text-gray-700" data-id="element-4154">
                      Les documents marqués{' '}
                      <span className="text-yellow-600 font-medium" data-id="element-4155">
                        Premium
                      </span>{' '}
                      sont accessibles uniquement avec un compte Premium. Vous
                      avez actuellement accès à tous les documents avec votre
                      abonnement Premium Afrique.
                    </p>
                  </div>
                  <div className="space-y-3" data-id="element-4156">
                    {tenderDetails.documents.map(doc => <div key={doc.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between" data-id="element-4157">
                        <div className="flex items-center" data-id="element-4158">
                          <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center mr-4" data-id="element-4159">
                            <FileIcon size={20} className="text-blue-500" data-id="element-4160" />
                          </div>
                          <div data-id="element-4161">
                            <div className="flex items-center" data-id="element-4162">
                              <span className="font-medium text-gray-900" data-id="element-4163">
                                {doc.name}
                              </span>
                              {doc.isPremium && <span className="ml-2 text-xs bg-yellow-50 text-yellow-700 px-2 py-0.5 rounded-full" data-id="element-4164">
                                  Premium
                                </span>}
                            </div>
                            <div className="text-sm text-gray-500 flex items-center mt-0.5" data-id="element-4165">
                              <span data-id="element-4166">{doc.type}</span>
                              <span className="mx-2" data-id="element-4167">•</span>
                              <span data-id="element-4168">{doc.size}</span>
                              <span className="mx-2" data-id="element-4169">•</span>
                              <span data-id="element-4170">
                                Ajouté le {formatDate(doc.uploadDate)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <Button variant="outline" size="sm" className="flex items-center" disabled={doc.isPremium && !tenderDetails.premium} data-id="element-4171">
                          <DownloadIcon size={16} className="mr-2" data-id="element-4172" />
                          Télécharger
                        </Button>
                      </div>)}
                  </div>
                </div>}
              {/* Requirements tab */}
              {activeTab === 'requirements' && <div data-id="element-4173">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-4174">
                    Exigences et critères de sélection
                  </h2>
                  <div className="bg-gray-50 p-4 rounded-lg mb-6 flex items-center" data-id="element-4175">
                    <InfoIcon size={18} className="text-blue-500 mr-3 flex-shrink-0" data-id="element-4176" />
                    <p className="text-sm text-gray-700" data-id="element-4177">
                      Les exigences ci-dessous sont extraites du cahier des
                      charges. Pour plus de détails, veuillez consulter les
                      documents officiels.
                    </p>
                  </div>
                  <div className="space-y-4" data-id="element-4178">
                    {(showAllRequirements ? tenderDetails.requirements : tenderDetails.requirements.slice(0, 3)).map(req => <div key={req.id} className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4179">
                        <div className="flex justify-between items-start mb-2" data-id="element-4180">
                          <div className="font-medium text-gray-900" data-id="element-4181">
                            {req.category}
                          </div>
                          {req.isRequired ? <span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full" data-id="element-4182">
                              Obligatoire
                            </span> : <span className="text-xs bg-gray-100 text-gray-700 px-2 py-0.5 rounded-full" data-id="element-4183">
                              Optionnel
                            </span>}
                        </div>
                        <p className="text-sm text-gray-700" data-id="element-4184">
                          {req.description}
                        </p>
                      </div>)}
                    {!showAllRequirements && tenderDetails.requirements.length > 3 && <button className="w-full py-2 text-sm text-blue-600 hover:text-blue-800 font-medium flex items-center justify-center" onClick={() => setShowAllRequirements(true)} data-id="element-4185">
                          Voir toutes les exigences (
                          {tenderDetails.requirements.length})
                          <ChevronDownIcon size={16} className="ml-1" data-id="element-4186" />
                        </button>}
                  </div>
                  <div className="mt-6 bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-4187">
                    <div className="flex items-start" data-id="element-4188">
                      <StarIcon size={20} className="text-yellow-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-4189" />
                      <div data-id="element-4190">
                        <h3 className="font-medium text-gray-900 mb-1" data-id="element-4191">
                          Analyse de qualification Premium
                        </h3>
                        <p className="text-sm text-gray-700 mb-3" data-id="element-4192">
                          Notre analyse indique que votre entreprise répond à 4
                          des 5 critères exigés pour ce marché.
                        </p>
                        <div className="space-y-2" data-id="element-4193">
                          <div className="flex items-center text-sm" data-id="element-4194">
                            <CheckIcon size={16} className="text-green-500 mr-2" data-id="element-4195" />
                            <span className="text-gray-700" data-id="element-4196">
                              Agrément B3 Catégorie 4 (Confirmé)
                            </span>
                          </div>
                          <div className="flex items-center text-sm" data-id="element-4197">
                            <CheckIcon size={16} className="text-green-500 mr-2" data-id="element-4198" />
                            <span className="text-gray-700" data-id="element-4199">
                              7 projets similaires réalisés (Exigence: 5)
                            </span>
                          </div>
                          <div className="flex items-center text-sm" data-id="element-4200">
                            <CheckIcon size={16} className="text-green-500 mr-2" data-id="element-4201" />
                            <span className="text-gray-700" data-id="element-4202">
                              CA moyen: 1.2M TND (Exigence: 900K TND)
                            </span>
                          </div>
                          <div className="flex items-center text-sm" data-id="element-4203">
                            <CheckIcon size={16} className="text-green-500 mr-2" data-id="element-4204" />
                            <span className="text-gray-700" data-id="element-4205">
                              Personnel qualifié disponible
                            </span>
                          </div>
                          <div className="flex items-center text-sm" data-id="element-4206">
                            <XIcon size={16} className="text-red-500 mr-2" data-id="element-4207" />
                            <span className="text-gray-700" data-id="element-4208">
                              Certification ISO 9001 (Non disponible, mais
                              optionnelle)
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>}
              {/* Contacts tab */}
              {activeTab === 'contacts' && <div data-id="element-4209">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4" data-id="element-4210">
                    Contacts et informations
                  </h2>
                  <div className="mb-6" data-id="element-4211">
                    <div className="flex items-start mb-4" data-id="element-4212">
                      <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4" data-id="element-4213">
                        <BuildingIcon size={24} className="text-blue-600" data-id="element-4214" />
                      </div>
                      <div data-id="element-4215">
                        <h3 className="font-medium text-gray-900" data-id="element-4216">
                          {tenderDetails.organization}
                        </h3>
                        <p className="text-sm text-gray-600 mt-1" data-id="element-4217">
                          {tenderDetails.location}, {tenderDetails.country}
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6" data-id="element-4218">
                      {tenderDetails.contacts.map((contact, index) => <div key={index} className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4219">
                          <h3 className="font-medium text-gray-900 mb-1" data-id="element-4220">
                            {contact.name}
                          </h3>
                          <p className="text-sm text-gray-600 mb-3" data-id="element-4221">
                            {contact.role}
                          </p>
                          <div className="space-y-2" data-id="element-4222">
                            <div className="flex items-center text-sm" data-id="element-4223">
                              <MailIcon size={16} className="text-gray-400 mr-2" data-id="element-4224" />
                              <a href={`mailto:${contact.email}`} className="text-blue-600 hover:text-blue-800" data-id="element-4225">
                                {contact.email}
                              </a>
                            </div>
                            {contact.phone && <div className="flex items-center text-sm" data-id="element-4226">
                                <PhoneIcon size={16} className="text-gray-400 mr-2" data-id="element-4227" />
                                <a href={`tel:${contact.phone}`} className="text-blue-600 hover:text-blue-800" data-id="element-4228">
                                  {contact.phone}
                                </a>
                              </div>}
                          </div>
                        </div>)}
                    </div>
                  </div>
                  <div className="mt-8" data-id="element-4229">
                    <h3 className="font-medium text-gray-900 mb-4" data-id="element-4230">
                      Besoin d'assistance pour ce marché?
                    </h3>
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start" data-id="element-4231">
                      <HelpCircleIcon size={20} className="text-blue-500 mr-3 flex-shrink-0 mt-0.5" data-id="element-4232" />
                      <div data-id="element-4233">
                        <p className="text-sm text-gray-700 mb-3" data-id="element-4234">
                          Notre équipe d'experts est disponible pour vous aider
                          à préparer votre offre, répondre à vos questions ou
                          vous mettre en contact avec l'entité adjudicatrice.
                        </p>
                        <Button variant="primary" size="sm" className="flex items-center" data-id="element-4235">
                          <MessageSquareIcon size={16} className="mr-2" data-id="element-4236" />
                          Demander de l'assistance
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>}
              {/* Comments tab */}
              {activeTab === 'comments' && <div data-id="element-4237">
                  <div className="flex justify-between items-center mb-4" data-id="element-4238">
                    <h2 className="text-lg font-semibold text-gray-900" data-id="element-4239">
                      Commentaires internes ({tenderDetails.comments.length})
                    </h2>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg mb-6 flex items-center" data-id="element-4240">
                    <InfoIcon size={18} className="text-yellow-500 mr-3 flex-shrink-0" data-id="element-4241" />
                    <p className="text-sm text-gray-700" data-id="element-4242">
                      Les commentaires sont visibles uniquement par les membres
                      de votre équipe. Ils ne sont pas partagés avec l'entité
                      adjudicatrice ou d'autres utilisateurs.
                    </p>
                  </div>
                  {/* Add comment form */}
                  <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6" data-id="element-4243">
                    <h3 className="font-medium text-gray-900 mb-2" data-id="element-4244">
                      Ajouter un commentaire
                    </h3>
                    <textarea className="w-full border border-gray-300 rounded-lg p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" rows={3} placeholder="Partagez vos observations ou questions avec votre équipe..." value={newComment} onChange={e => setNewComment(e.target.value)} data-id="element-4245" />
                    <div className="flex justify-end mt-2" data-id="element-4246">
                      <Button variant="primary" size="sm" className="flex items-center" onClick={addComment} disabled={!newComment.trim()} data-id="element-4247">
                        <PlusIcon size={16} className="mr-2" data-id="element-4248" />
                        Ajouter
                      </Button>
                    </div>
                  </div>
                  {/* Comments list */}
                  <div className="space-y-4" data-id="element-4249">
                    {tenderDetails.comments.length > 0 ? tenderDetails.comments.map(comment => <div key={comment.id} className="bg-white border border-gray-200 rounded-lg p-4" data-id="element-4250">
                          <div className="flex items-start" data-id="element-4251">
                            <img src={comment.user.avatar} alt={comment.user.name} className="w-10 h-10 rounded-full mr-3" data-id="element-4252" />
                            <div className="flex-grow" data-id="element-4253">
                              <div className="flex justify-between items-center mb-1" data-id="element-4254">
                                <div data-id="element-4255">
                                  <span className="font-medium text-gray-900 mr-2" data-id="element-4256">
                                    {comment.user.name}
                                  </span>
                                  <span className="text-xs text-gray-500" data-id="element-4257">
                                    {comment.user.role}
                                  </span>
                                </div>
                                <span className="text-xs text-gray-500" data-id="element-4258">
                                  {comment.date.toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                                </span>
                              </div>
                              <p className="text-sm text-gray-700" data-id="element-4259">
                                {comment.text}
                              </p>
                            </div>
                          </div>
                        </div>) : <div className="text-center py-6 text-gray-500" data-id="element-4260">
                        Aucun commentaire pour le moment
                      </div>}
                  </div>
                </div>}
            </div>
          </div>
          {/* Two-column layout for additional information and sidebar */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6 print:hidden" data-id="element-4261">
            {/* Main column */}
            <div className="lg:col-span-2 space-y-6" data-id="element-4262">
              {/* Similar tenders section */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-4263">
                <div className="p-4 border-b border-gray-100 flex justify-between items-center" data-id="element-4264">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-4265">
                    Appels d'offres similaires
                  </h2>
                  <Link to="/premium-tenders" className="text-sm text-blue-600 hover:text-blue-800 flex items-center" data-id="element-4266">
                    Voir plus <ChevronRightIcon size={16} className="ml-1" data-id="element-4267" />
                  </Link>
                </div>
                <div className="p-4" data-id="element-4268">
                  <div className="space-y-4" data-id="element-4269">
                    {tenderDetails.similarTenders.map(tender => <div key={tender.id} className="bg-gray-50 p-3 rounded-lg flex justify-between items-center" data-id="element-4270">
                        <div data-id="element-4271">
                          <h3 className="font-medium text-gray-900 text-sm mb-1" data-id="element-4272">
                            {tender.title}
                          </h3>
                          <div className="flex items-center text-xs text-gray-500 space-x-3" data-id="element-4273">
                            <span className="flex items-center" data-id="element-4274">
                              <BuildingIcon size={12} className="mr-1" data-id="element-4275" />
                              {tender.organization}
                            </span>
                            <span className="flex items-center" data-id="element-4276">
                              <MapPinIcon size={12} className="mr-1" data-id="element-4277" />
                              {tender.country}
                            </span>
                            <span className="flex items-center" data-id="element-4278">
                              <ClockIcon size={12} className="mr-1" data-id="element-4279" />
                              Expire le {formatDate(tender.expiryDate)}
                            </span>
                          </div>
                        </div>
                        {tender.matchScore && <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold ${tender.matchScore >= 80 ? 'bg-green-500' : tender.matchScore >= 60 ? 'bg-yellow-500' : 'bg-orange-500'}`} data-id="element-4280">
                            {tender.matchScore}%
                          </div>}
                      </div>)}
                  </div>
                </div>
              </div>
              {/* Bidding guidance */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-4281">
                <div className="p-4 border-b border-gray-100" data-id="element-4282">
                  <h2 className="text-lg font-semibold text-gray-900" data-id="element-4283">
                    Guide de soumission
                  </h2>
                </div>
                <div className="p-4" data-id="element-4284">
                  <div className="bg-blue-50 p-4 rounded-lg" data-id="element-4285">
                    <h3 className="font-medium text-gray-900 mb-2 flex items-center" data-id="element-4286">
                      <InfoIcon size={18} className="text-blue-500 mr-2" data-id="element-4287" />
                      Conseils pour réussir votre soumission
                    </h3>
                    <ul className="space-y-2 text-sm text-gray-700" data-id="element-4288">
                      <li className="flex items-start" data-id="element-4289">
                        <CheckIcon size={16} className="text-green-500 mr-2 mt-0.5" data-id="element-4290" />
                        <span data-id="element-4291">
                          Assurez-vous de répondre à tous les critères
                          obligatoires mentionnés dans le cahier des charges.
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-4292">
                        <CheckIcon size={16} className="text-green-500 mr-2 mt-0.5" data-id="element-4293" />
                        <span data-id="element-4294">
                          Préparez tous les documents administratifs requis
                          (registre de commerce, attestations fiscales, etc.).
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-4295">
                        <CheckIcon size={16} className="text-green-500 mr-2 mt-0.5" data-id="element-4296" />
                        <span data-id="element-4297">
                          Détaillez votre offre technique en mettant en avant
                          votre expérience dans des projets similaires.
                        </span>
                      </li>
                      <li className="flex items-start" data-id="element-4298">
                        <CheckIcon size={16} className="text-green-500 mr-2 mt-0.5" data-id="element-4299" />
                        <span data-id="element-4300">
                          Soumettez votre offre avant la date limite via la
                          plateforme TUNEPS.
                        </span>
                      </li>
                    </ul>
                    <div className="mt-4" data-id="element-4301">
                      <Button variant="primary" size="sm" className="flex items-center" data-id="element-4302">
                        <DownloadIcon size={16} className="mr-2" data-id="element-4303" />
                        Télécharger le guide complet
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* Sidebar */}
            <div className="space-y-6" data-id="element-4304">
              {/* Quick actions */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-4305">
                <div className="p-4 border-b border-gray-100" data-id="element-4306">
                  <h2 className="font-semibold text-gray-900" data-id="element-4307">
                    Actions rapides
                  </h2>
                </div>
                <div className="p-4 space-y-3" data-id="element-4308">
                  <Button variant="primary" fullWidth className="flex items-center justify-center shadow-md" data-id="element-4309">
                    <DownloadIcon size={16} className="mr-2" data-id="element-4310" />
                    Télécharger tous les documents
                  </Button>
                  <Button variant={tenderDetails.isSaved ? 'secondary' : 'outline'} fullWidth className="flex items-center justify-center" data-id="element-4311">
                    <BookmarkIcon size={16} className="mr-2" data-id="element-4312" />
                    {tenderDetails.isSaved ? 'Retiré des favoris' : 'Ajouter aux favoris'}
                  </Button>
                  <Button variant={tenderDetails.hasAlert ? 'secondary' : 'outline'} fullWidth className="flex items-center justify-center" data-id="element-4313">
                    <BellIcon size={16} className="mr-2" data-id="element-4314" />
                    {tenderDetails.hasAlert ? "Supprimer l'alerte" : 'Créer une alerte'}
                  </Button>
                  <Button variant="outline" fullWidth className="flex items-center justify-center" data-id="element-4315">
                    <ShareIcon size={16} className="mr-2" data-id="element-4316" />
                    Partager avec mon équipe
                  </Button>
                </div>
              </div>
              {/* Premium features */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-800 rounded-xl shadow-lg p-4 text-white" data-id="element-4317">
                <div className="flex items-center mb-3" data-id="element-4318">
                  <StarIcon size={20} className="text-yellow-400 mr-2" data-id="element-4319" />
                  <h2 className="font-semibold" data-id="element-4320">Fonctionnalités Premium</h2>
                </div>
                <p className="text-sm text-blue-100 mb-4" data-id="element-4321">
                  Profitez de toutes les fonctionnalités exclusives de votre
                  compte Premium Afrique.
                </p>
                <ul className="space-y-2 text-sm mb-4" data-id="element-4322">
                  <li className="flex items-start" data-id="element-4323">
                    <CheckIcon size={16} className="text-yellow-400 mr-2 mt-0.5" data-id="element-4324" />
                    <span className="text-blue-50" data-id="element-4325">
                      Accès aux documents techniques complets
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-4326">
                    <CheckIcon size={16} className="text-yellow-400 mr-2 mt-0.5" data-id="element-4327" />
                    <span className="text-blue-50" data-id="element-4328">
                      Analyse de qualification personnalisée
                    </span>
                  </li>
                  <li className="flex items-start" data-id="element-4329">
                    <CheckIcon size={16} className="text-yellow-400 mr-2 mt-0.5" data-id="element-4330" />
                    <span className="text-blue-50" data-id="element-4331">
                      Assistance prioritaire pour vos questions
                    </span>
                  </li>
                </ul>
                <Button variant="secondary" className="w-full bg-white text-blue-700 hover:bg-blue-50 border-none" data-id="element-4332">
                  <HelpCircleIcon size={16} className="mr-2" data-id="element-4333" />
                  Obtenir de l'aide
                </Button>
              </div>
              {/* Tender statistics */}
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-4334">
                <div className="p-4 border-b border-gray-100" data-id="element-4335">
                  <h2 className="font-semibold text-gray-900" data-id="element-4336">Statistiques</h2>
                </div>
                <div className="p-4" data-id="element-4337">
                  <div className="space-y-4" data-id="element-4338">
                    <div className="flex items-center justify-between" data-id="element-4339">
                      <span className="text-sm text-gray-600 flex items-center" data-id="element-4340">
                        <EyeIcon size={16} className="mr-2 text-gray-400" data-id="element-4341" />
                        Vues
                      </span>
                      <span className="font-medium text-gray-900" data-id="element-4342">
                        {tenderDetails.viewCount}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4343">
                      <span className="text-sm text-gray-600 flex items-center" data-id="element-4344">
                        <UsersIcon size={16} className="mr-2 text-gray-400" data-id="element-4345" />
                        Soumissionnaires potentiels
                      </span>
                      <span className="font-medium text-gray-900" data-id="element-4346">
                        {tenderDetails.applicationCount}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4347">
                      <span className="text-sm text-gray-600 flex items-center" data-id="element-4348">
                        <FileTextIcon size={16} className="mr-2 text-gray-400" data-id="element-4349" />
                        Documents
                      </span>
                      <span className="font-medium text-gray-900" data-id="element-4350">
                        {tenderDetails.documentCount}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4351">
                      <span className="text-sm text-gray-600 flex items-center" data-id="element-4352">
                        <BarChartIcon size={16} className="mr-2 text-gray-400" data-id="element-4353" />
                        Score de correspondance
                      </span>
                      <span className="font-medium text-green-600" data-id="element-4354">
                        {tenderDetails.matchScore}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-4355">
                      <span className="text-sm text-gray-600 flex items-center" data-id="element-4356">
                        <ClockIcon size={16} className="mr-2 text-gray-400" data-id="element-4357" />
                        Jours restants
                      </span>
                      <span className={`font-medium ${isExpired ? 'text-red-600' : closingSoon ? 'text-orange-600' : 'text-gray-900'}`} data-id="element-4358">
                        {isExpired ? 'Expiré' : `${daysRemaining} jour${daysRemaining !== 1 ? 's' : ''}`}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>;
}